/**
 * Las preguntas del enlace de revision de renovacion.
 *
 * El enlace lleva dos cosas distintas: DATOS que el cliente confirma (salen de
 * su ficha) y PREGUNTAS que solo el puede contestar. Este modulo es la parte
 * pensante de las preguntas, fuera del componente para poder probarla.
 *
 * Varias DEPENDEN de otra, y ahi esta toda la gracia: *"¿Por que no quieres
 * renovarla?"* solo tiene sentido para quien acaba de contestar que no quiere
 * (Lissette 2026-09-15), igual que la del correo de ID.me solo para quien dijo
 * que si tiene cuenta. El catalogo del servidor las marca con `dependsOn` /
 * `dependsOnValue` y las manda dentro de `questions`; aqui se decide que se ve,
 * que se guarda y que hace falta contestar antes de poder enviar.
 *
 * Espejo de `dropUnaskedDependents` en
 * deskte-api/src/routes/renewal-review.routes.ts: el servidor vuelve a tirar lo
 * mismo porque el enlace es publico y el cuerpo del POST lo escribe quien
 * quiera. Si cambia una regla, cambian las dos.
 */

/**
 * Tope de una respuesta ESCRITA (hoy, el motivo de no renovar).
 *
 * ESPEJO de LARGO_MAXIMO_TEXTO en
 * deskte-api/src/routes/renewal-review.routes.ts, que recorta al guardar. El
 * `maxLength` de la pagina existe para que el cliente vea el limite mientras
 * escribe en vez de perder el final sin enterarse; el del servidor, porque el
 * enlace es publico y el cuerpo del envio lo escribe quien quiera. Si cambia
 * uno, cambia el otro.
 */
export const LARGO_MAXIMO_TEXTO = 500;

/** La pregunta padre de `q`, si este mismo enlace la lleva. */
function padreDe(q, questions) {
  if (!q || !q.dependsOn) return null;
  return (questions || []).find((x) => x && x.key === q.dependsOn) || null;
}

/**
 * Las preguntas que el cliente debe ver ahora mismo.
 *
 * Una dependiente aparece solo cuando su padre YA esta contestado con el valor
 * que la activa. Si el enlace no lleva al padre, la dependiente se enseña
 * suelta: el agente la mando a proposito y esconderla para siempre seria
 * peor que preguntarla de mas.
 */
export function preguntasVisibles(questions, answers) {
  const a = answers || {};
  return (questions || []).filter((q) => {
    const padre = padreDe(q, questions);
    if (!padre) return true;
    return a[padre.key] === q.dependsOnValue;
  });
}

/**
 * Una respuesta que cuenta como dada.
 *
 * Un boton sin tocar y una casilla en blanco son lo mismo: no ha contestado.
 * La cadena vacia importa porque una pregunta de escribir se puede "contestar"
 * borrando lo que habia.
 */
function estaContestada(v) {
  return v !== undefined && v !== null && String(v).trim() !== '';
}

/**
 * Las preguntas OBLIGATORIAS que el cliente todavia no ha contestado.
 *
 * Lissette, 2026-09-09: *"la pregunta de ID que sea obligatorio"*. Se exige
 * AQUI, en la pagina, y no en el servidor: rechazar la respuesta entera por una
 * pregunta sin contestar castigaria justo a quien tiene la pagina vieja abierta
 * en el movil, y perderiamos una renovacion confirmada por no perder un dato
 * suelto. Obligatoria quiere decir que no se puede enviar sin contestarla, y
 * para eso basta con esto.
 *
 * Solo cuentan las VISIBLES: una obligatoria que depende de otra y todavia no
 * se enseña no puede bloquear el envio — nadie puede contestar algo que no ve.
 *
 * Devuelve las claves, no un booleano, para poder señalar cual falta.
 */
export function faltanObligatorias(questions, answers) {
  const a = answers || {};
  return preguntasVisibles(questions, answers)
    .filter((q) => q && q.required && !estaContestada(a[q.key]))
    .map((q) => q.key);
}

/**
 * Contesta `key` con `value` y limpia lo que esa respuesta deja sin sentido.
 *
 * Si el cliente se corrige ("no tengo cuenta" -> "si tengo"), la respuesta que
 * dio a la pregunta dependiente ya no puede quedarse guardada: se le dejo de
 * enseñar, y mandarla igual haria que el agente llamara por una ayuda que
 * nadie pidio. Se limpia en cascada, por si algun dia una dependiente tiene a
 * su vez otra debajo.
 *
 * Devuelve un objeto NUEVO — nunca toca el que le pasan.
 */
export function responderPregunta(questions, answers, key, value) {
  const out = { ...(answers || {}) };
  // Vaciar una casilla es DESCONTESTAR, no contestar con "". Guardar la cadena
  // vacia haria que el enlace mandara una respuesta que el cliente borro, y
  // ademas la daria por contestada al comprobar las obligatorias.
  if (typeof value === 'string' && value.trim() === '') delete out[key];
  else out[key] = value;
  let cambio = true;
  while (cambio) {
    cambio = false;
    const visibles = new Set(preguntasVisibles(questions, out).map((q) => q.key));
    for (const q of (questions || [])) {
      if (q && q.dependsOn && out[q.key] !== undefined && !visibles.has(q.key)) {
        delete out[q.key];
        cambio = true;
      }
    }
  }
  return out;
}
